import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    ColumnDirective,
    ColumnsDirective,
    GridComponent,
    Inject,
    Sort,
} from '@syncfusion/ej2-react-grids';
import type { SortSettingsModel, RowSelectEventArgs } from '@syncfusion/ej2-react-grids';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import * as Inovx from '@shared';
import HomeKnowledgeBookStore from '../stores/HomeKnowledgeBookStore';
import HomeKnowledgeBookActions from '../actions/HomeKnowledgeBookActions';
import type { IHomeKnowledgeBook } from '../constants/interfaces';

const DateTimeFormatHelper = Inovx.Shared.Common.DateTimeFormatHelper;

const recentSortSettings: SortSettingsModel = {
    columns: [{ field: 'Timestamp', direction: 'Descending' }],
    allowUnsort: false,
};

const favoriteSortSettings: SortSettingsModel = {
    columns: [{ field: 'Name', direction: 'Ascending' }],
    allowUnsort: false,
};

type KnowledgeBookListMode = 'recent' | 'favorites';

interface Props {
    onError?: (error: any) => void;
}

const KnowledgeBooks: React.FC<Props> = ({ onError }) => {
    const mountedRef = useRef(true);
    const gridRef = useRef<GridComponent | null>(null);
    const syncfusionLocale = (window as any).SYNCFUSION_LOCALE;
    const [listMode, setListMode] = useState<KnowledgeBookListMode>('recent');
    const [favoritesLoaded, setFavoritesLoaded] = useState(false);
    const [recentKBs, setRecentKBs] = useState<IHomeKnowledgeBook[]>(
        HomeKnowledgeBookStore.getRecentOpenedKnowledgeBooks()
    );
    const [favoriteKBs, setFavoriteKBs] = useState<IHomeKnowledgeBook[]>(
        HomeKnowledgeBookStore.getFavoriteKnowledgeBooks()
    );

    const onChange = useCallback(() => {
        if (!mountedRef.current) return;
        setRecentKBs(HomeKnowledgeBookStore.getRecentOpenedKnowledgeBooks());
        setFavoriteKBs(HomeKnowledgeBookStore.getFavoriteKnowledgeBooks());
    }, []);

    useEffect(() => {
        mountedRef.current = true;
        HomeKnowledgeBookStore.addChangeListener(onChange);
        HomeKnowledgeBookActions.getRecentKnowledgeBooks().catch(error => onError?.(error));
        return () => {
            mountedRef.current = false;
            HomeKnowledgeBookStore.removeChangeListener(onChange);
        };
    }, [onChange, onError]);

    const handleListModeChange = async (mode: KnowledgeBookListMode) => {
        setListMode(mode);

        if (mode === 'favorites' && !favoritesLoaded) {
            try {
                await HomeKnowledgeBookActions.getFavoriteKnowledgeBooks();
                setFavoritesLoaded(true);
            } catch (error) {
                onError?.(error);
            }
        }
    };

    const renderNameColumn = useCallback((props: IHomeKnowledgeBook) => {
        const tagsString =
            Array.isArray(props.ClassificationTags) && props.ClassificationTags.length > 0
                ? [...props.ClassificationTags]
                    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
                    .map(tag => `#${tag}`)
                    .join(' ')
                : '';
        return (
            <span className="kb-name" title={props.Name}>
                <div className="kb-text-overflow font-bold">{props.Name}</div>
                <div className="kb-text-overflow kv" title={tagsString}>{tagsString}</div>
            </span>
        );
    }, []);

    const renderFacilityColumn = useCallback((props: IHomeKnowledgeBook) => (
        <span className="kb-text-overflow" title={props.FacilityName ?? ''}>{props.FacilityName}</span>
    ), []);

    const renderTimestampColumn = useCallback((props: IHomeKnowledgeBook) => {
        const formatted =
            DateTimeFormatHelper.getFormatDate(props.Timestamp) +
            ' ' +
            DateTimeFormatHelper.getFormatTime(props.Timestamp, true);
        return <span className="kb-text-overflow" title={formatted}>{formatted}</span>;
    }, []);

    const handleRowSelected = (args: RowSelectEventArgs) => {
        const kb = args.data as IHomeKnowledgeBook;
        window.location.href = `/Facility?kbId=${kb.Id}`;
    };

    const dataSource = listMode === 'favorites' ? favoriteKBs : recentKBs;
    const timestampHeader =
        listMode === 'favorites'
            ? InovxLoader.t('home:knowledgeBook.favorited')
            : InovxLoader.t('home:knowledgeBook.opened');
    const emptyMessage =
        listMode === 'favorites'
            ? InovxLoader.t('home:knowledgeBook.noFavorites')
            : InovxLoader.t('home:knowledgeBook.noRecent');

    return (
        <div className="col-lg-12 control-section kb-content">
            <div className="content-wrapper">
                <div className="e-card" id="kb-card">
                    <div className="e-card-header">
                        <div className="e-card-header-caption">
                            <div className="e-card-title">{InovxLoader.t('home:knowledgeBooks')}</div>
                        </div>
                    </div>
                    <div className="card-buttons">
                        <ButtonComponent
                            className={listMode === 'recent' ? 'round-btn active' : 'round-btn'}
                            onClick={() => handleListModeChange('recent')}>
                            <i className="fa fa-clock-o mr-5p" aria-hidden="true" />
                            {InovxLoader.t('home:knowledgeBook.recentlyOpened')}
                        </ButtonComponent>
                        <ButtonComponent
                            className={listMode === 'favorites' ? 'round-btn active' : 'round-btn'}
                            onClick={() => handleListModeChange('favorites')}>
                            <i className="fa fa-star mr-5p" aria-hidden="true" />
                            {InovxLoader.t('home:knowledgeBook.favorites')}
                        </ButtonComponent>
                    </div>
                    <div className="e-card-content">
                        <div className="control-pane kb-treegrid-wrapper">
                            {dataSource.length > 0 ? (
                                <GridComponent
                                    key={listMode}
                                    ref={gridRef}
                                    className="kb-table"
                                    dataSource={dataSource}
                                    sortSettings={listMode === 'favorites' ? favoriteSortSettings : recentSortSettings}
                                    allowSorting={true}
                                    allowSelection={true}
                                    rowSelected={handleRowSelected}
                                    height="100%"
                                    locale={syncfusionLocale || undefined}>
                                    <ColumnsDirective>
                                        <ColumnDirective
                                            field="Name"
                                            width="40%"
                                            headerText={InovxLoader.t('home:knowledgeBook.name')}
                                            template={renderNameColumn} />
                                        <ColumnDirective
                                            field="FacilityName"
                                            headerText={InovxLoader.t('home:facility')}
                                            template={renderFacilityColumn} />
                                        <ColumnDirective
                                            field="Timestamp"
                                            type="dateTime"
                                            headerText={timestampHeader}
                                            template={renderTimestampColumn} />
                                    </ColumnsDirective>
                                    <Inject services={[Sort]} />
                                </GridComponent>
                            ) : (
                                <div className="kb-empty-message">{emptyMessage}</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default KnowledgeBooks;
